<script>
    import { apiMethods } from "../../../Services/index.svelte";

    import {
        CardLayout,
        CustomTooltip,
        DateInput,
        DecimalInput,
    } from "../../../Components/Common/index.svelte";

    import {
        calculateTotalFee,
        getAmountInCurrency,
        getBoolean,
        getDisplayTotal,
        getEstimateStatus,
        getFormatDate,
        getText,
        goToDownloadPage,
        showError,
    } from "../../../Utils/utilFunctions.svelte";

    import { editIcon } from "../../../Utils/utilConstants.svelte";

    export let isEditEnabled = false;
    export let isUpdatingEstimate = false;
    export let estimateDetails = {};
    export let patientDetails = {};

    let defaultEstimateDetails = {};

    $: referenceName = `${patientDetails.lastName ? patientDetails.lastName.replaceAll(/['",.\-\s]/g, "") : patientDetails.firstName?.replaceAll(/['",.\-\s]/g, "")}${
        estimateDetails.id
    }`;

    const bottomLinks = [
        {
            displayName: "Download Quote",
            clickFunc: () => downloadEstimate(),
        },
    ];

    const downloadEstimate = () => {
        goToDownloadPage(0, estimateDetails.id);
    };

    const handleEditEstimate = () => {
        isEditEnabled = true;

        defaultEstimateDetails = { ...estimateDetails };
    };

    const updateEstimate = async () => {
        isUpdatingEstimate = true;

        estimateDetails.totalFee =
            estimateDetails.actualGap +
            estimateDetails.internalProcessingFee +
            estimateDetails.rebate;

        await apiMethods.putMethod
            .updateEstimate(estimateDetails)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to update quote details, please refresh the page and try again and contact support if the issue persists.",
                    );
                else _handleUpdateEstimate(res);
            })
            .catch((err) => showError(err));

        isEditEnabled = false;

        isUpdatingEstimate = false;
    };

    const cancelEditEstimate = () => {
        estimateDetails = { ...defaultEstimateDetails };
        isEditEnabled = false;
    };

    // HELPER FUNCTIONS
    const _handleUpdateEstimate = (res) => {
        estimateDetails = { ...res };
        defaultEstimateDetails = { ...res };
    };
</script>

<CardLayout
    title="Quote Details"
    btnOptions={[
        {
            iconName: "editIcon",
            iFont: editIcon,
            btnText: "Edit",
            iconFunction: () => {
                handleEditEstimate();
                estimateDetails.expireDate =
                    estimateDetails.expireDate.split("T")[0];
            },
        },
    ]}
    {bottomLinks}
>
    <div class="flex flex-col flex-1 px-2 py-4 w-full lg:flex-row">
        <div class="flex flex-col flex-1 gap-y-4 h-full">
            <div class="flex flex-row gap-x-2">
                <p class="title">Quote No.:</p>
                <p class="content">
                    {getText(estimateDetails.estimateNumber)}
                </p>
            </div>

            <div class="flex flex-row gap-x-2">
                <p class="title">Status:</p>
                <p
                    class={`content ${estimateDetails.status ? ([1, 2, 4].includes(estimateDetails.status) ? "text-[#2DBD6F]" : [5, 9].includes(estimateDetails.status) ? "text-[#E05757]" : "") : ""}`}
                >
                    {getEstimateStatus(estimateDetails.status)}
                </p>
            </div>

            <div class="flex flex-row gap-x-2">
                <p class="title">Created Date:</p>
                <p class="content">
                    {getFormatDate(estimateDetails.createdDate)}
                </p>
            </div>

            <div class="flex flex-row gap-x-2">
                <p class="title">Due Date:</p>
                {#if !isEditEnabled}
                    <p class="content">
                        {getFormatDate(estimateDetails.expireDate)}
                    </p>
                {:else}
                    <!-- <input
                        type="date"
                        class="content input input-sm"
                        bind:value={estimateDetails.expireDate}
                    /> -->
                    <DateInput
                        bind:value={estimateDetails.expireDate}
                        wrapperClass="input input-sm px-0 input-bordered"
                        wrapperClassFocus="inputFocus"
                    />
                {/if}
            </div>
        </div>

        <div class="divider lg:divider-horizontal" />

        <div class="flex flex-col flex-1 gap-y-4 h-full">
            <div class="flex flex-row gap-x-2">
                <p class="title">Add GST:</p>
                <p class="content">{getBoolean(estimateDetails.addGST)}</p>
            </div>

            <div class="flex flex-row gap-x-2">
                <p class="title">Actual Gap:</p>
                <p class="content">
                    {getAmountInCurrency(estimateDetails.actualGap)}
                </p>
                <!-- {#if !isEditEnabled}
                    <p class="content">
                        {getAmountInCurrency(estimateDetails.actualGap)}
                    </p>
                {:else}
                    <DecimalInput
                        inputClass="content bg-[#e5e7eb]"
                        bind:value={estimateDetails.actualGap}
                        on:change={calculateTotalFee(estimateDetails)}
                    />
                {/if} -->
            </div>

            <div class="flex flex-row gap-x-2">
                <p class="title">Rebate:</p>
                <p class="content">
                    {getAmountInCurrency(estimateDetails.rebate)}
                </p>
                <!-- {#if !isEditEnabled}
                    <p class="content">
                        {getAmountInCurrency(estimateDetails.rebate)}
                    </p>
                {:else}
                    <DecimalInput
                        inputClass="content bg-[#e5e7eb]"
                        bind:value={estimateDetails.rebate}
                        on:change={calculateTotalFee(estimateDetails)}
                    />
                {/if} -->
            </div>

            <div class="flex flex-row gap-x-2">
                <div class="title flex items-center gap-x-2">
                    <p>Processing Fee:</p>

                    <CustomTooltip>
                        <i
                            class="fa-regular fa-circle-question"
                            slot="tooltipTrigger"
                        />

                        <p>Internal Processing Fee</p>
                    </CustomTooltip>
                </div>
                {#if !isEditEnabled}
                    <p class="content">
                        {getAmountInCurrency(
                            estimateDetails.internalProcessingFee,
                        )}
                    </p>
                {:else}
                    <DecimalInput
                        inputClass="content bg-[#e5e7eb]"
                        bind:value={estimateDetails.internalProcessingFee}
                        on:change={calculateTotalFee(estimateDetails)}
                    />
                {/if}
            </div>

            <div class="flex flex-row gap-x-2">
                <p class="title">Total Fee:</p>
                <p class="content">
                    {getDisplayTotal(estimateDetails)}
                </p>
            </div>
        </div>

        <div class="divider lg:divider-horizontal" />

        <div class="flex flex-col flex-1 gap-y-4 h-full">
            <div class="flex flex-row gap-x-2">
                <p class="title">Quote Approved:</p>
                <p class="content">
                    {getBoolean(estimateDetails.quoteApproved)}
                </p>
            </div>

            {#if estimateDetails.quoteApproved}
                <div class="flex flex-row gap-x-2">
                    <p class="title">Quote Approved Date:</p>
                    <p class="content">
                        {getFormatDate(estimateDetails.quoteApprovedDate)}
                    </p>
                </div>
            {/if}

            <div class="flex flex-row gap-x-2">
                <p class="title">Paying by Bank Transfer:</p>
                <p class="content">
                    {getBoolean(estimateDetails.payingByBankTransfer)}
                </p>
            </div>

            <div class="flex flex-row gap-x-2">
                <p class="title">Reference:</p>
                <p class="content">
                    {referenceName}
                </p>
            </div>

            {#if isEditEnabled}
                <div class="flex flex-1 items-end justify-end gap-x-2">
                    <button
                        class={`btn btn-sm btn-error w-24 ${
                            isUpdatingEstimate ? "loading" : ""
                        }`}
                        on:click|preventDefault={cancelEditEstimate}
                        disabled={isUpdatingEstimate}
                    >
                        Cancel
                    </button>
                    <button
                        class={`btn btn-sm btn-secondary w-24 ${
                            isUpdatingEstimate ? "loading w-28" : ""
                        }`}
                        on:click|preventDefault={updateEstimate}
                        disabled={isUpdatingEstimate}
                    >
                        {isUpdatingEstimate ? "Saving" : "Save"}
                    </button>
                </div>
            {/if}
        </div>
    </div>
</CardLayout>

<style>
    .title {
        width: 200px;
    }

    .content {
        width: calc(100% - 200px);
    }

    .transition {
        transition: 0.5s linear;
    }

    input {
        background-color: #e5e7eb;
        border-radius: 2px;
        font-size: 15px;
    }
</style>
